import { useRef } from "react";
import { motion } from "framer-motion";
import {
  Truck,
  Bike,
  BookOpen,
  Star,
  Flame,
  Sandwich,
  Share2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { HIGHLIGHTS } from "../data";

const ICONS = {
  truck: Truck,
  bike: Bike,
  book: BookOpen,
  star: Star,
  flame: Flame,
  sandwich: Sandwich,
  share: Share2,
};

export const HighlightsCarousel = () => {
  const scroller = useRef(null);

  const scrollBy = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <section
      data-testid="highlights-section"
      className="relative py-16 md:py-24 bg-brand-surface overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-brand-gold uppercase tracking-[0.3em] text-xs mb-3">
              Destacados
            </p>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-brand-cream leading-tight">
              Lo mejor de la casa
            </h2>
          </div>
          <div className="hidden sm:flex gap-2">
            <button
              onClick={() => scrollBy(-1)}
              data-testid="carousel-prev"
              aria-label="Anterior"
              className="w-11 h-11 rounded-full border border-brand-gold/40 text-brand-gold hover:bg-brand-gold hover:text-brand-base transition-colors flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              data-testid="carousel-next"
              aria-label="Siguiente"
              className="w-11 h-11 rounded-full border border-brand-gold/40 text-brand-gold hover:bg-brand-gold hover:text-brand-base transition-colors flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={scroller}
          data-testid="carousel-track"
          className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-hide"
        >
          {HIGHLIGHTS.map((h, i) => {
            const Icon = ICONS[h.icon] || Star;
            const CardTag = h.href ? "a" : "div";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="snap-start shrink-0 w-[80%] xs:w-[70%] sm:w-[340px]"
              >
                <CardTag
                  {...(h.href ? { href: h.href } : {})}
                  aria-label={h.title}
                  data-testid={`highlight-card-${i}`}
                  className="group block h-full bg-brand-cream border border-brand-gold/30 rounded-2xl p-7 hover:border-brand-gold hover:-translate-y-1 transition-all duration-300"
                >
                  <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-brand-burgundy text-brand-gold">
                    <Icon className="w-6 h-6" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold text-brand-burgundy">
                    {h.title}
                  </h3>
                  <p className="mt-2 text-sm text-brand-burgundy/70 leading-relaxed">
                    {h.text}
                  </p>
                </CardTag>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
